import {
  Action,
  Classifier,
  EmploymentType,
  LONG_DATE_FORMAT,
  LogicalOperator,
  RemunerationType,
  UnitType,
} from '$constants'
import dayjs, { Dayjs } from 'dayjs'
import omit from 'lodash/omit'
import {
  formatCurrency,
  formatPercent,
} from 'src/components/routes/_common/helpers'
import {
  splitAndLowerCaseAndInsertSpaces,
  transformNumberProperty,
} from '$utils'
import { Nullable } from '../types'

export interface Variation {
  id?: Nullable<string>
  description?: Nullable<string>
  classifier: Classifier
  logicalOperator: LogicalOperator
  age?: Nullable<number>
  contractedWeeklyHours?: Nullable<number>
  employmentType?: Nullable<EmploymentType>
  remunerationType?: Nullable<RemunerationType>
  startDate?: Nullable<Dayjs>
  action: Action
  actionValue?: number
  unitType: UnitType
  effectiveRate?: Nullable<number>
  datestampDeleted?: Nullable<Dayjs>
}

// Function to apply transformations to Variation object
export const transformVariationOnSave = (variation: Variation): Variation => {
  return {
    ...omit(variation, 'index'),
    actionValue: transformNumberProperty(`${variation.actionValue}`),
  }
}

// Function to apply transformations to Variation object
export const transformVariationOnGet = (variation: Variation): Variation => {
  const transformedVariation: Variation = {
    ...variation,
    startDate: dayjs(variation.startDate),
  }

  return {
    ...transformedVariation,
    description: buildDescription(transformedVariation),
  }
}

// Function to get the value to display in description
const getValues = (variation: Variation) => {
  let value: string = ''
  let classifier: string = ''
  const action: string =
    variation.action === Action.Add ? 'the base pay plus' : 'the base pay minus'
  const logicalOperator: string = splitAndLowerCaseAndInsertSpaces(
    variation.logicalOperator
  )

  let actionValue: string = ''
  let unitType: string = ''
  if (variation.unitType === UnitType.Currency) {
    actionValue = formatCurrency(variation.actionValue, true)
    unitType =
      variation.actionValue && variation.actionValue > 1 ? 'dollars' : 'dollar'
  } else {
    actionValue = formatPercent(variation.actionValue, true)
    unitType = 'percent'
  }

  switch (variation.classifier) {
    case Classifier.Employment:
      value = splitAndLowerCaseAndInsertSpaces(
        variation.employmentType as string
      )
      classifier = 'employment type'
      break
    case Classifier.Age:
      value = `${variation.age}`
      classifier = 'age'
      break
    case Classifier.ContractedWeeklyHours:
      value = `${variation.contractedWeeklyHours}`
      classifier = 'contracted weekly hours'
      break
    case Classifier.Remuneration:
      value = variation.remunerationType === 1 ? 'salary' : 'hourly rate'
      classifier = 'remuneration type'
      break
    case Classifier.StartDate:
      if (!variation.startDate) break
      value = variation.startDate.format(LONG_DATE_FORMAT)
      classifier = 'start date'
      break
  }

  return { value, classifier, action, logicalOperator, actionValue, unitType }
}

// Function to apply description transformations to Variation object
export const buildDescription = (variation: Variation): string => {
  const { value, classifier, action, logicalOperator, actionValue, unitType } =
    getValues(variation)

  const description: string = `If the employees ${classifier} ${logicalOperator} ${value} then set the base hourly payrate to ${action} ${actionValue} ${unitType}`

  return description
}
